import { useEffect, useState, useCallback } from "react";
import { db } from "@/lib/db";
import { useProviderStore } from "@/stores/providerStore";
import { GmailClient } from "@/lib/providers/gmail";
import { OutlookClient } from "@/lib/providers/outlook";
import { syncProvider, clearLibrary } from "@/lib/sync";
import { buildSearchIndex, searchDocuments, clearSearchIndex } from "@/lib/search";
import { DocumentCard } from "@/components/DocumentCard";
import { DocumentDetail } from "@/components/DocumentDetail";
import { ContextualActionBar } from "@/components/ContextualActionBar";
import { useToastStore } from "@/stores/toastStore";
import { formatBytes } from "@/lib/formatters";
import { Search, RefreshCw, Trash2, HardDrive, Filter, AlertCircle } from "lucide-react";
import type { Document, SyncState } from "@/types";
import type { SyncProgress } from "@/lib/sync";

function formatRelativeTime(iso?: string): string {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function Library() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [storage, setStorage] = useState<{ used: number; quota: number } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [syncStates, setSyncStates] = useState<Record<string, SyncState>>({});
  const [searchReady, setSearchReady] = useState(false);
  const { tokens, getAccessToken } = useProviderStore();
  const { addToast } = useToastStore();

  const loadDocs = useCallback(async () => {
    const docs = await db.documents.toArray();
    setDocuments(docs);
    // Rebuild search index when docs change
    if (docs.length > 0) {
      await buildSearchIndex();
      setSearchReady(true);
    }
  }, []);

  const loadSyncStates = useCallback(async () => {
    const states = await db.syncState.toArray();
    const map: Record<string, SyncState> = {};
    for (const s of states) {
      map[s.provider] = s;
    }
    setSyncStates(map);
  }, []);

  useEffect(() => {
    loadDocs();
    loadSyncStates();
    if (navigator.storage?.estimate) {
      navigator.storage.estimate().then((est) => {
        setStorage({ used: est.usage || 0, quota: est.quota || 0 });
      });
    }
  }, [loadDocs, loadSyncStates]);

  // Search effect
  useEffect(() => {
    let cancelled = false;
    const doSearch = async () => {
      if (!search.trim()) {
        const docs = await db.documents.toArray();
        if (!cancelled) setDocuments(docs);
        return;
      }
      if (!searchReady) return;
      const results = await searchDocuments(search);
      if (!cancelled) setDocuments(results);
    };
    doSearch();
    return () => { cancelled = true; };
  }, [search, searchReady]);

  const handleSync = async (provider: "gmail" | "outlook") => {
    const token = await getAccessToken(provider);
    if (!token) {
      addToast({ message: `Please connect ${provider} in Settings`, type: "warning" });
      return;
    }
    setSyncing(true);
    setSyncProgress({ stage: "messages", messagesFound: 0, attachmentsFound: 0, attachmentsDownloaded: 0, errors: 0 });

    try {
      const client = provider === "gmail" ? new GmailClient(token) : new OutlookClient(token);
      const profile = await client.getProfile();

      for await (const progress of syncProvider(client, profile.emailAddress, 50)) {
        setSyncProgress(progress);
        if (progress.stage === "done" || progress.stage === "error" || progress.stage === "reconnect_required") break;
      }

      if (syncProgress?.stage === "reconnect_required") {
        addToast({ message: `${provider} connection expired. Please reconnect in Settings.`, type: "warning" });
      } else if (syncProgress?.stage !== "error") {
        addToast({ message: "Sync complete", type: "success" });
      }
    } catch (err) {
      console.error("Sync failed:", err);
      addToast({ message: "Sync failed", type: "error" });
    } finally {
      setSyncing(false);
      await loadDocs();
      loadSyncStates();
    }
  };

  const handleClear = async () => {
    if (!confirm("Clear all local data? This cannot be undone.")) return;
    await clearLibrary();
    clearSearchIndex();
    setSearchReady(false);
    loadDocs();
    loadSyncStates();
    addToast({ message: "Library cleared", type: "info" });
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredDocs = documents.filter((doc) => {
    const matchesType = filterType === "all" || doc.mimeType.includes(filterType);
    return matchesType;
  });

  const mimeTypes = Array.from(new Set(documents.map((d) => d.mimeType.split("/")[0]))).filter(Boolean);

  return (
    <div className="space-y-5">
      {/* Storage + actions panel */}
      {storage && (
        <div className="rounded-xl border border-border-default bg-surface-0 p-5 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-accent-50 text-accent-600 dark:bg-accent-500/10 dark:text-accent-400">
                <HardDrive size={18} />
              </div>
              <div>
                <p className="text-sm font-semibold text-text-primary dark:text-dark-text-primary">Local Storage</p>
                <p className="text-xs text-text-muted mt-0.5">
                  {formatBytes(storage.used)} used {storage.quota > 0 && `of ${formatBytes(storage.quota)}`}
                  {" · "}{documents.length} documents
                  {searchReady && " · Search indexed"}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {tokens.gmail?.accessToken && (
                <button
                  onClick={() => handleSync("gmail")}
                  disabled={syncing}
                  className="flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-accent-600 disabled:opacity-50"
                >
                  <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
                  {syncing ? "Syncing…" : "Sync Gmail"}
                </button>
              )}
              {tokens.outlook?.accessToken && (
                <button
                  onClick={() => handleSync("outlook")}
                  disabled={syncing}
                  className="flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-accent-600 disabled:opacity-50"
                >
                  <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
                  {syncing ? "Syncing…" : "Sync Outlook"}
                </button>
              )}
              <button
                onClick={handleClear}
                disabled={syncing}
                className="flex items-center gap-1.5 rounded-lg border border-border-strong bg-surface-0 px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:bg-surface-2 disabled:opacity-50 dark:border-dark-border-strong dark:bg-dark-surface-1 dark:text-dark-text-secondary dark:hover:bg-dark-surface-2"
              >
                <Trash2 size={13} />
                Clear
              </button>
            </div>
          </div>

          {(syncStates.gmail || syncStates.outlook) && (
            <div className="mt-3 flex flex-wrap gap-3">
              {(["gmail", "outlook"] as const).map((p) => {
                const state = syncStates[p];
                if (!state) return null;
                const isReconnect = state.status === "reconnect_required";
                return (
                  <div key={p} className={`flex items-center gap-1.5 text-xs ${isReconnect ? "text-danger" : "text-text-muted"}`}>
                    {isReconnect && <AlertCircle size={12} />}
                    <span className="capitalize font-medium">{p}</span>
                    <span>·</span>
                    <span>{state.status === "syncing" ? "Syncing…" : formatRelativeTime(state.lastCompletedAt)}</span>
                  </div>
                );
              })}
            </div>
          )}

          {syncProgress && syncing && (
            <div className="mt-3 rounded-lg bg-surface-1 dark:bg-dark-surface-2 p-3 text-xs text-text-secondary dark:text-dark-text-secondary">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="font-medium capitalize">{syncProgress.stage}</span>
                <span className="text-text-muted">·</span>
                <span>{syncProgress.messagesFound} messages</span>
                <span className="text-text-muted">·</span>
                <span>{syncProgress.attachmentsDownloaded} / {syncProgress.attachmentsFound} attachments</span>
              </div>
              {syncProgress.errors > 0 && (
                <p className="text-danger">{syncProgress.errors} error{syncProgress.errors > 1 ? "s" : ""}</p>
              )}
            </div>
          )}
        </div>
      )}

      <ContextualActionBar
        selectedIds={Array.from(selectedIds)}
        onClear={() => setSelectedIds(new Set())}
        onAction={() => { setSelectedIds(new Set()); loadDocs(); }}
      />

      {/* Search and filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search documents…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full max-w-xs rounded-lg border border-border-strong bg-surface-0 pl-9 pr-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-100 dark:border-dark-border-strong dark:bg-dark-surface-2 dark:text-dark-text-primary"
          />
        </div>
        <div className="relative">
          <Filter size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="rounded-lg border border-border-strong bg-surface-0 pl-9 pr-3 py-2 text-sm text-text-primary dark:border-dark-border-strong dark:bg-dark-surface-2 dark:text-dark-text-primary"
          >
            <option value="all">All types</option>
            {mimeTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Document grid */}
      <div className="rounded-xl border border-border-default bg-surface-0 p-5 shadow-sm dark:border-dark-border-default dark:bg-dark-surface-1">
        {filteredDocs.length === 0 ? (
          <div className="py-16 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-surface-2 dark:bg-dark-surface-2 mb-3">
              <HardDrive size={24} className="text-text-muted" />
            </div>
            <p className="text-sm font-medium text-text-secondary dark:text-dark-text-secondary">
              {documents.length === 0 ? "No attachments yet" : "No matches"}
            </p>
            {documents.length === 0 && (
              <p className="mt-1 text-xs text-text-muted">Connect a provider in Settings to start syncing</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredDocs.map((doc) => (
              <DocumentCard
                key={doc.id}
                document={doc}
                selected={selectedIds.has(doc.id)}
                onClick={() => setSelectedDoc(doc)}
                onSelect={() => toggleSelect(doc.id)}
              />
            ))}
          </div>
        )}
      </div>

      {selectedDoc && (
        <DocumentDetail
          document={selectedDoc}
          onClose={() => setSelectedDoc(null)}
        />
      )}
    </div>
  );
}
